import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useSignUp, useVerifyEmail } from '@/hooks/useAuth';
import { checkEmail, checkPassword } from '@/utils/validation';
import { SHA512 } from 'crypto-js';

const InitialForm = {
  email: '',
  emailError: false,
  emailDuplicated: false,
  emailTouched: false,
  nickname: '',
  password: '',
  passwordError: '',
  passwordTouched: false,
  confirmPassword: '',
  confirmPasswordError: false,
  confirmPasswordTouched: false,
  privacyPolicyAgreed: false,
};

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(InitialForm);
  const signUpMutation = useSignUp();
  const verifyEmailMutation = useVerifyEmail();

  useEffect(() => {
    const privacyPolicyAgreed = sessionStorage.getItem('privacyPolicyAgreed');
    if (privacyPolicyAgreed === 'true') {
      setForm(prev => ({ ...prev, privacyPolicyAgreed: true }));
      sessionStorage.removeItem('privacyPolicyAgreed');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (form.emailTouched) {
      setForm(prev => ({ ...prev, emailError: !checkEmail(form.email) }));
    }
  }, [form.email, form.emailTouched]);

  useEffect(() => {
    if (form.passwordTouched) {
      setForm(prev => ({
        ...prev,
        passwordError: checkPassword(form.password),
      }));
    }
  }, [form.password, form.passwordTouched]);

  useEffect(() => {
    if (form.confirmPasswordTouched) {
      setForm(prev => ({
        ...prev,
        confirmPasswordError: form.password !== form.confirmPassword,
      }));
    }
  }, [form.confirmPassword, form.password, form.confirmPasswordTouched]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const isPasswordValid = !form.passwordError && form.password.length > 0;
  const isConfirmPasswordValid =
    !form.confirmPasswordError && form.confirmPassword.length > 0;

  const isFormValid =
    form.email.length &&
    !form.emailError &&
    !form.emailDuplicated &&
    form.password.length &&
    !form.passwordError &&
    !form.confirmPasswordError &&
    form.confirmPassword.length &&
    form.nickname.length &&
    form.privacyPolicyAgreed;

  const handleEmailBlur = async () => {
    setForm(prev => ({ ...prev, emailTouched: true }));
    if (!checkEmail(form.email)) {
      setForm(prev => ({ ...prev, emailError: true }));
      return;
    }
    try {
      const res = await verifyEmailMutation.mutateAsync(form.email);
      if (res.statusCode !== 200) {
        setForm(prev => ({ ...prev, emailDuplicated: true }));
      } else {
        setForm(prev => ({ ...prev, emailDuplicated: false }));
      }
    } catch (error: any) {
      if (error?.response?.data?.statusCode !== 200) {
        setForm(prev => ({ ...prev, emailDuplicated: true }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      const hash = SHA512(form.password).toString();
      const res = await signUpMutation.mutateAsync({
        email: form.email,
        password: hash,
        nickname: form.nickname,
      });

      if (res.statusCode === 200 || res.success) {
        alert('Please check your email to verify your account.');
        navigate('/user/login');
      } else {
        alert(res.message || 'An account with this email already exists');
      }
    } catch (error: any) {
      alert(
        error?.response?.data?.message ||
          'An account with this email already exists'
      );
    }
  };

  return (
    <div className='min-h-screen bg-white flex flex-col'>
      <div className='flex h-full w-full items-center p-[20px] gap-[8px] relative'>
        <button
          onClick={() => navigate('/user/login')}
          className='absolute left-[20px] text-black cursor-pointer'
        >
          <Icon icon='mdi:arrow-left' className='w-6 h-6' />
        </button>
        <p className='flex-1 text-center font-semibold text-black text-sm'>
          Sign Up
        </p>
      </div>

      <div className='flex flex-col items-start flex-1 px-6 pb-8'>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
          className='w-full max-w-[400px] mx-auto flex flex-col gap-3'
        >
          <div className='flex flex-col gap-2'>
            <label
              htmlFor='email'
              className='text-gray-800 text-base font-medium'
            >
              Email
            </label>
            <input
              type='email'
              id='email'
              name='email'
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              onFocus={() => {
                if (!form.emailTouched) {
                  setForm(prev => ({ ...prev, emailTouched: true }));
                }
              }}
              className={`w-full h-12 px-4 py-2 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                form.emailError || form.emailDuplicated
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-[#fccb16] focus:ring-[#fccb16]'
              }`}
              placeholder='Email'
              required
            />
            {form.emailTouched && form.emailError && (
              <p className='text-sm text-red-600'>
                Please enter a valid email address
              </p>
            )}
            {!form.emailError && form.emailDuplicated && (
              <p className='text-sm text-red-600'>This email already exists</p>
            )}
          </div>

          <div className='flex flex-col gap-2'>
            <label
              htmlFor='nickname'
              className='text-gray-800 text-base font-medium'
            >
              Nickname
            </label>
            <input
              type='text'
              id='nickname'
              name='nickname'
              value={form.nickname}
              onChange={handleChange}
              className='w-full h-12 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#fccb16] focus:ring-1 focus:ring-[#fccb16]'
              placeholder='Nickname'
              required
            />
          </div>

          <div className='flex flex-col gap-2'>
            <label
              htmlFor='password'
              className='text-gray-800 text-base font-medium'
            >
              Password
            </label>
            <div className='relative'>
              <input
                type='password'
                id='password'
                name='password'
                autoComplete='new-password'
                value={form.password}
                onChange={handleChange}
                onBlur={() => {
                  setForm(prev => ({ ...prev, passwordTouched: true }));
                }}
                className={`w-full h-12 px-4 py-2 pr-10 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                  isPasswordValid
                    ? 'border-[#fccb16] focus:border-[#fccb16] focus:ring-[#fccb16]'
                    : form.passwordError && form.password.length > 0
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-[#fccb16] focus:ring-[#fccb16]'
                }`}
                placeholder='Password'
                required
              />
              {isPasswordValid && (
                <div className='absolute top-1/2 right-3 -translate-y-1/2'>
                  <Icon icon='mdi:check' className='w-5 h-5 text-[#fccb16]' />
                </div>
              )}
            </div>
            {form.passwordTouched && form.passwordError && (
              <p className='text-sm text-red-600'>{form.passwordError}</p>
            )}
          </div>

          <div className='flex flex-col gap-2'>
            <label
              htmlFor='confirmPassword'
              className='text-gray-800 text-base font-medium'
            >
              Confirm Password
            </label>
            <div className='relative'>
              <input
                type='password'
                id='confirmPassword'
                name='confirmPassword'
                autoComplete='new-password'
                value={form.confirmPassword}
                onChange={handleChange}
                onBlur={() => {
                  setForm(prev => ({ ...prev, confirmPasswordTouched: true }));
                }}
                className={`w-full h-12 px-4 py-2 pr-10 bg-white border rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
                  isConfirmPasswordValid
                    ? 'border-[#fccb16] focus:border-[#fccb16] focus:ring-[#fccb16]'
                    : form.confirmPasswordError &&
                        form.confirmPassword.length > 0
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-[#fccb16] focus:ring-[#fccb16]'
                }`}
                placeholder='Confirm Password'
                required
              />
              {isConfirmPasswordValid && (
                <div className='absolute top-1/2 right-3 -translate-y-1/2'>
                  <Icon icon='mdi:check' className='w-5 h-5 text-[#fccb16]' />
                </div>
              )}
            </div>
            {form.confirmPasswordTouched && form.confirmPasswordError && (
              <p className='text-sm text-red-600'>Passwords do not match</p>
            )}
          </div>

          <div className='flex items-center gap-2 mt-2'>
            <input
              type='checkbox'
              id='privacyPolicy'
              checked={form.privacyPolicyAgreed}
              onChange={e => {
                e.preventDefault();
                navigate('/privacy-policy');
              }}
              className='h-4 w-4 rounded border-gray-300 text-[#fccb16] accent-[#fccb16] focus:ring-[#fccb16]'
            />
            <label htmlFor='privacyPolicy' className='text-xs text-gray-700'>
              I have read and agree to the website's{' '}
              <button
                type='button'
                className='text-[#fccb16] underline hover:text-[#ffd84d]'
                onClick={() => navigate('/privacy-policy')}
              >
                privacy policy
              </button>
            </label>
          </div>

          <button
            type='submit'
            disabled={!isFormValid || signUpMutation.isPending}
            className='w-full h-12 bg-[#fccb16] text-black rounded-lg font-medium text-base cursor-pointer transition-all hover:bg-[#ffd84d] disabled:bg-slate-400 disabled:cursor-not-allowed'
          >
            {signUpMutation.isPending ? 'Signing up...' : 'Sign Up'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Signup;
